import { api } from '../api';
import config from '../config';

// GCompris' own menu sections, in the order its menu shows them. `all` is not a
// GCompris section - it is the unfiltered view students land on.
export const GCOMPRIS_CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'computer', label: 'Computer' },
  { id: 'discovery', label: 'Discovery' },
  { id: 'sciences', label: 'Sciences' },
  { id: 'fun', label: 'Fun' },
  { id: 'math', label: 'Math' },
  { id: 'puzzle', label: 'Puzzle' },
  { id: 'reading', label: 'Reading' },
  { id: 'strategy', label: 'Strategy' },
];

export const GCOMPRIS_LOGO_URL = '/gcompris/gcompris-logo.svg';
export const gcomprisIconUrl = (activityName) => `/gcompris/activities/${activityName}.svg`;
export const gcomprisCategoryIconUrl = (categoryId) => `/gcompris/categories/${categoryId}.svg`;

export function filterActivities(activities, { category = 'all', query = '' } = {}) {
  const needle = query.trim().toLowerCase();
  return activities.filter((activity) => {
    if (category !== 'all' && !activity.categories.includes(category)) return false;
    return !needle || `${activity.title} ${activity.description}`.toLowerCase().includes(needle);
  });
}

// Rows are only ever built up to the level being played (or the known total), but a
// student can report any level number, so never loop on an unbounded value.
const MAX_LEVEL_ROWS = 100;

/**
 * Rows of the levels panel: `completed`, `play` (the level GCompris resumes at)
 * or `locked`. Every level below `nextLevel` counts as completed even when only
 * the next-level value was recorded (older helper results store nothing else).
 * Levels after the current one are listed as locked only when the activity's
 * level count is known (`totalLevels`) - otherwise we'd be inventing them.
 */
export function getLevelRows({ completedLevels = [], nextLevel = 1, totalLevels = null }) {
  const completed = new Set(completedLevels);
  const lastRow = Math.min(totalLevels || nextLevel, MAX_LEVEL_ROWS);
  return Array.from({ length: lastRow }, (_, index) => {
    const level = index + 1;
    let status = 'locked';
    if (level < nextLevel || completed.has(level)) status = 'completed';
    else if (level === nextLevel) status = 'play';
    return { level, status };
  });
}

/**
 * The coins one game pays for its levels, from the saved settings (GET /api/gcompris/settings/reward),
 * as the backend pays them: level N = coins per game + coins per level up x (N - 1) (20 and 10 ->
 * 20, 30, 40...), unless a custom amount replaces it - the game's own level, then every level of
 * the game, then the level in every game.
 * `coinsForLevel(level)` gives a level's coins; `sameEveryLevel` is the amount when every level pays
 * the same (null when they differ).
 */
export function gcomprisLevelRewards({ coinsPerGame = 0, coinsPerNewLevel = 0, levelCoins = [] } = {}, activityName) {
  const coins = (value) => Math.max(0, Number(value) || 0);
  const isEveryLevel = (row) => row.level === undefined || row.level === null || row.level === '';
  const levelEntries = (rows) => rows.filter((row) => !isEveryLevel(row)).map((row) => [Number(row.level), coins(row.coins)]);
  const gameRows = levelCoins.filter((row) => row.activityName && row.activityName === activityName);
  const gameEveryLevel = gameRows.find(isEveryLevel);
  // A game with its own every-level amount ignores the every-game level amounts.
  const everyGameEntries = gameEveryLevel ? [] : levelEntries(levelCoins.filter((row) => !row.activityName));
  const custom = Object.fromEntries([...everyGameEntries, ...levelEntries(gameRows)]);
  const firstLevel = coins(gameEveryLevel ? gameEveryLevel.coins : coinsPerGame);
  const perLevelUp = gameEveryLevel ? 0 : coins(coinsPerNewLevel);
  return {
    coinsForLevel: (level) => custom[level] ?? firstLevel + perLevelUp * (Math.max(1, level) - 1),
    sameEveryLevel: perLevelUp === 0 && Object.keys(custom).length === 0 ? firstLevel : null,
  };
}

// How many distinct levels count as completed under the same rule as getLevelRows.
export const completedLevelCount = (completedLevels = [], nextLevel = 1) =>
  nextLevel - 1 + completedLevels.filter((level) => level >= nextLevel).length;

export function formatPlayTime(durationMs = 0) {
  const totalSeconds = Math.max(0, Math.floor(durationMs / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  return hours ? `${hours}:${String(minutes).padStart(2, '0')}:${seconds}` : `${minutes}:${seconds}`;
}

export const makeSessionId = () =>
  `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

/**
 * Hands an activity to the local EduBridge helper (edubridge:// protocol handler),
 * which starts GCompris at the student's next level and reports results back.
 * Same handshake GComprisLauncher uses. Resolves with the session id the running
 * overlay polls for.
 */
export async function openGComprisActivity({ activityName, fallbackLevel = 1, studentId = '', assignmentId = '', courseId = '' }) {
  const sessionId = makeSessionId();
  let token = localStorage.getItem('token') || '';
  try {
    const response = await api.post('/api/gcompris/report-token');
    token = response.data?.token || token;
  } catch (error) {
    // Backward-compatible fallback for deployments that have not restarted yet.
  }
  let startLevel = fallbackLevel;
  try {
    const levelResponse = await api.get(`/api/gcompris/lastlevel/${encodeURIComponent(activityName)}`);
    startLevel = Math.max(1, Number(levelResponse.data) || startLevel);
  } catch (error) {
    // The local helper also requests the level; retain the displayed fallback.
  }
  const params = new URLSearchParams({
    activity: activityName,
    startLevel: String(startLevel),
    sessionId,
    token,
    serverUrl: config.API_BASE_URL,
    studentId: String(studentId),
    assignmentId: String(assignmentId),
    courseId: String(courseId),
  });

  window.location.href = `edubridge://open?${params.toString()}`;
  return sessionId;
}
